/**
 * Platform-aware keyboard helpers.
 *
 * - Detects macOS vs other platforms once at module load.
 * - Provides display glyphs (⌘ vs Ctrl) for shortcut UIs and the symmetric
 *   ARIA `aria-keyshortcuts` token list ("Meta+K Control+K") so screen
 *   readers announce the right combo regardless of platform.
 *
 * `navigator.platform` is deprecated but still the most reliable signal
 * for "Apple keyboard layout". We fall back to `userAgent` for environments
 * where `platform` returns the bland `MacIntel` lie or `Windows`.
 */

const isMacFromPlatform = (): boolean => {
  if (typeof navigator === 'undefined') return false;
  // navigator.platform is deprecated but still the most reliable Mac signal.
  const platform = navigator.platform || '';
  if (/Mac|iPhone|iPod|iPad/i.test(platform)) return true;
  const ua = navigator.userAgent || '';
  return /Mac|iPhone|iPod|iPad/i.test(ua);
};

export const IS_MAC = isMacFromPlatform();

/** Visual glyph for the Command / Control modifier on the current platform. */
export const MOD_GLYPH = IS_MAC ? '⌘' : 'Ctrl';

/**
 * Format a shortcut for display, e.g. `formatShortcut('K')` →
 * `'⌘K'` on Mac, `'Ctrl K'` elsewhere. The separating space on non-Mac
 * mirrors how most native apps render Ctrl+key shortcuts.
 */
export function formatShortcut(key: string): string {
  return IS_MAC ? `${MOD_GLYPH}${key}` : `${MOD_GLYPH} ${key}`;
}

/**
 * Symmetric aria-keyshortcuts value covering both modifiers, so AT users
 * on either platform hear "Press Meta+K or Control+K".
 */
export function ariaKeyShortcut(key: string): string {
  return `Meta+${key} Control+${key}`;
}

/**
 * Platform-aware modifier glyph map. On Mac we use the native single-glyph
 * symbols Apple prints on every keyboard cap (⌘ ⇧ ⌥ ⌃ ⏎); elsewhere we
 * keep the spelled-out modifier names users expect on Windows/Linux.
 *
 * Note: "Ctrl" in the input list is treated as the *primary* modifier and
 * maps to ⌘ on Mac (since virtually every Otto handler accepts both
 * `event.metaKey` and `event.ctrlKey`). Use "Control" if you specifically
 * mean the macOS Control key (⌃) — rare in our codebase.
 */
const MAC_MODIFIER_GLYPHS: Record<string, string> = {
  Ctrl: '⌘',
  Cmd: '⌘',
  Meta: '⌘',
  Shift: '⇧',
  Alt: '⌥',
  Option: '⌥',
  Control: '⌃',
  Enter: '⏎',
  Return: '⏎',
  Backspace: '⌫',
  Delete: '⌦',
  Escape: 'Esc',
  ArrowUp: '↑',
  ArrowDown: '↓',
  ArrowLeft: '←',
  ArrowRight: '→',
};

/**
 * Format a multi-part key combo for display, e.g.
 *   formatKeyCombo(['Shift', 'Alt', 'F']) →
 *     '⇧⌥F' on Mac, 'Shift+Alt+F' elsewhere.
 *   formatKeyCombo(['Ctrl', 'Enter']) →
 *     '⌘⏎' on Mac, 'Ctrl+Enter' elsewhere.
 *
 * On Mac the parts join without separators (matching Apple's HIG); on
 * other platforms we use '+' to match Windows/Linux conventions.
 */
export function formatKeyCombo(parts: readonly string[]): string {
  if (IS_MAC) {
    return parts.map((p) => MAC_MODIFIER_GLYPHS[p] ?? p).join('');
  }
  return parts.join('+');
}

/**
 * Platform-aware "primary modifier held" check.
 *
 * On macOS the canonical app shortcut modifier is ⌘ (`metaKey`); on
 * Windows/Linux it is Ctrl (`ctrlKey`). For years we wrote handlers as
 * `event.ctrlKey || event.metaKey`, which accepted the *wrong*
 * modifier on each platform — e.g. Ctrl+S would fire the save handler
 * on Mac (where Ctrl+S has no native meaning but is also not the
 * shortcut Mac users expect), and ⌘S would fire it on Windows (where
 * ⌘ doesn't even exist on most keyboards but the check still passed
 * if a weird remapper or RDP session was involved).
 *
 * This helper enforces "one platform → one modifier", so each app
 * shortcut maps to exactly the keystroke the OS surfaces to users in
 * its own UI conventions. It also rejects the combo when *both*
 * modifiers are held, which avoids accidental matches inside
 * browser/OS chords like ⌃⌘Space (emoji picker) on Mac.
 *
 *   IS_MAC=true  →  metaKey && !ctrlKey
 *   IS_MAC=false →  ctrlKey && !metaKey
 *
 * Use this for any handler that fires an action on "primary modifier
 * + key". For the *opposite* intent ("user pressed a plain key with no
 * modifiers"), continue to check `!event.metaKey && !event.ctrlKey`
 * directly — that semantic is platform-agnostic.
 */
export function isPrimaryMod(event: KeyboardEvent | React.KeyboardEvent): boolean {
  return IS_MAC
    ? event.metaKey && !event.ctrlKey
    : event.ctrlKey && !event.metaKey;
}
